import React, { useEffect, useMemo, useState } from 'react';
import styles from './PlanWizard.module.css';
import ToothChart from './ToothChart';
import { apiFetch } from '../api/client';
import { supabase } from '../auth/supabase';

export default function PlanWizard({ patientId, onClose, onCreated }) {
  const [step, setStep] = useState(1); // 1: Plan meta, 2: Items
  const [title, setTitle] = useState('Active Plan');
  const [caseNumber, setCaseNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lookups, setLookups] = useState({ teeth: [], diagnoses: [], procedures: [], statuses: [] });
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const getToken = async () => (await supabase.auth.getSession()).data.session?.access_token;

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await apiFetch('/lookups', {}, getToken);
        setLookups(data);
        // Start with one blank row
        setItems([{ tooth: '', diagnosis_id: '', procedure_id: '', status_id: '', notes: '', sort_order: 0 }]);
      } catch (e) {
        setError(e.message || 'Failed to load lookups');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const canProceedToItems = title.trim().length > 0;
  const canSave = useMemo(() => {
    // At least one valid row: tooth + at least one of diagnosis/procedure
    return items.some((r) => r.tooth && (r.diagnosis_id || r.procedure_id));
  }, [items]);

  const updateItem = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };

  const addRow = () => {
    setItems((prev) => [
      ...prev,
      { tooth: '', diagnosis_id: '', procedure_id: '', status_id: '', notes: '', sort_order: prev.length },
    ]);
  };

  const removeRow = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx).map((it, i) => ({ ...it, sort_order: i })));
  };

  const saveAll = async () => {
    setSaving(true);
    setError('');
    try {
      const plan = await apiFetch(`/patients/${patientId}/plans`, { method: 'POST', body: { title, case_number: caseNumber || null } }, getToken);
      const planId = plan.id;
      // Create items sequentially to respect order (could also Promise.all)
      for (const [i, row] of items.entries()) {
        if (!row.tooth || (!row.diagnosis_id && !row.procedure_id)) continue;
        await apiFetch(
          `/plans/${planId}/items`,
          {
            method: 'POST',
            body: {
              tooth: row.tooth ? parseInt(row.tooth, 10) : null,
              diagnosis_id: row.diagnosis_id ? parseInt(row.diagnosis_id, 10) : null,
              procedure_id: row.procedure_id ? parseInt(row.procedure_id, 10) : null,
              status_id: row.status_id ? parseInt(row.status_id, 10) : null,
              notes: row.notes || null,
              sort_order: i,
            },
          },
          getToken
        );
      }
      onCreated?.(plan);
    } catch (e) {
      setError(e.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={() => !saving && onClose?.()}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.title}>Medical Checkup</div>
          <button className={styles.close} onClick={() => !saving && onClose?.()} aria-label="Close">×</button>
        </div>

        <Stepper step={step} />

        {error && <div className={styles.error}>{error}</div>}
        {loading ? (
          <div className={styles.info}>Loading…</div>
        ) : step === 1 ? (
          <div className={styles.body}>
            <div className={styles.sectionTitle}>STEP 1 — Basic Information</div>
            <label className={styles.label}>
              <span>Plan Title</span>
              <input className={styles.input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Active Plan" />
            </label>
            <label className={styles.label}>
              <span>Case Number</span>
              <input className={styles.input} value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} placeholder="e.g., 466670" />
            </label>
          </div>
        ) : (
          <div className={styles.body}>
            <div className={styles.sectionTitle}>STEP 2 — Treatment Plan</div>
            <div className={styles.helper}>Click teeth to add rows. Then select Diagnosis, Proposed Tx, Status, and Notes.</div>
            <ToothChart
              selected={items.map((r) => parseInt(r.tooth || 0, 10)).filter(Boolean)}
              onSelect={(n) => setItems((prev) => ([...prev, { tooth: String(n), diagnosis_id: '', procedure_id: '', status_id: '', notes: '', sort_order: prev.length }]))}
            />
            <div className={styles.rows}>
              {items.map((row, idx) => (
                <div key={idx} className={styles.row}>
                  <select className={styles.select} value={row.tooth} onChange={(e) => updateItem(idx, 'tooth', e.target.value)}>
                    <option value="">Tooth</option>
                    {lookups.teeth.map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                  <select className={styles.select} value={row.diagnosis_id} onChange={async (e) => {
                    const val = e.target.value;
                    if (val === '__create_diagnosis__') {
                      const name = prompt('New diagnosis name');
                      if (name && name.trim()) {
                        try {
                          const created = await apiFetch('/lookups/diagnoses', { method: 'POST', body: { name } }, getToken);
                          setLookups((prev) => ({ ...prev, diagnoses: [...prev.diagnoses, created] }));
                          updateItem(idx, 'diagnosis_id', String(created.id));
                        } catch (err) { alert(err.message || 'Failed to create'); }
                      }
                      e.target.value = '';
                      return;
                    }
                    updateItem(idx, 'diagnosis_id', val);
                  }}>
                    <option value="">Diagnosis</option>
                    {lookups.diagnoses.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                    <option value="__create_diagnosis__">Create…</option>
                  </select>
                  <select className={styles.select} value={row.procedure_id} onChange={async (e) => {
                    const val = e.target.value;
                    if (val === '__create_procedure__') {
                      const name = prompt('New procedure name');
                      if (name && name.trim()) {
                        try {
                          const created = await apiFetch('/lookups/procedures', { method: 'POST', body: { name } }, getToken);
                          setLookups((prev) => ({ ...prev, procedures: [...prev.procedures, created] }));
                          updateItem(idx, 'procedure_id', String(created.id));
                        } catch (err) { alert(err.message || 'Failed to create'); }
                      }
                      e.target.value = '';
                      return;
                    }
                    updateItem(idx, 'procedure_id', val);
                  }}>
                    <option value="">Proposed Tx</option>
                    {lookups.procedures.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                    <option value="__create_procedure__">Create…</option>
                  </select>
                  <select className={styles.select} value={row.status_id} onChange={(e) => updateItem(idx, 'status_id', e.target.value)}>
                    <option value="">Status</option>
                    {lookups.statuses.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <input className={styles.notes} placeholder="Notes" value={row.notes} onChange={(e) => updateItem(idx, 'notes', e.target.value)} />
                  <button className={styles.rowDelete} onClick={() => removeRow(idx)} aria-label="Remove">🗑</button>
                </div>
              ))}
              <button className={styles.addRow} onClick={addRow}>+ Add Tooth</button>
            </div>
          </div>
        )}

        <div className={styles.footer}>
          {step > 1 ? (
            <button className={styles.btnGhost} onClick={() => setStep(step - 1)} disabled={saving}>Previous</button>
          ) : (
            <button className={styles.btnGhost} onClick={() => !saving && onClose?.()}>Cancel</button>
          )}
          {step === 1 ? (
            <button className={styles.btnPrimary} onClick={() => setStep(2)} disabled={!canProceedToItems}>Next</button>
          ) : (
            <button className={styles.btnPrimary} onClick={saveAll} disabled={!canSave || saving}>{saving ? 'Saving…' : 'Save'}</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div className={styles.stepper}>
      <div className={step >= 1 ? styles.stepActive : styles.step}>STEP 1
        <div className={styles.stepLabel}>Basic Information</div>
      </div>
      <div className={styles.stepDivider} />
      <div className={step >= 2 ? styles.stepActive : styles.step}>STEP 2
        <div className={styles.stepLabel}>Treatment Plan</div>
      </div>
    </div>
  );
}
